<template>
  <div class="money-input-container mb-3">
    <label class="mb-4">
      <question-display :question="question" customClass="customHeader" />
    </label>

    <!-- Loop through all expenses -->
    <div v-for="item in inputs" :key="item.id" class="mb-4">
      <label>{{ item.label }}</label>
      <div class="form-inline">
        <input
          type="text"
          class="form-control w-100"
          :placeholder="question.placeholder || 'Enter value'"
          v-model="item.value"
          @input="updateAnswer(item)"
        />
      </div>
      <small v-if="item.error" class="text-danger">{{ item.error }}</small>
    </div>

    <!-- Total -->
    <div class="mt-4">
      <label><strong>Total U.S. Spending</strong></label>
      <div class="form-inline">
        <input
          type="text"
          class="form-control w-100 bg-light"
          :value="formattedTotal"
          readonly
        />
      </div>
      <small v-if="totalError" class="text-danger">{{ totalError }}</small>
    </div>
  </div>
</template>

<script>
import surveyService from '@/services/surveyService';
import QuestionDisplay from '../../utils/QuestionDisplay.vue';

export default {
  components: {
    QuestionDisplay
  },
  props: {
    question: Object,
    required: Boolean,
    showValidation: Boolean
  },
  data() {
    return {
      maxAllowed: 0,
      totalError: '',
      inputs: [
        { id: '13.e.1', label: '1. Accommodations/Lodging in the U.S.', value: '', error: '' },
        { id: '13.e.2', label: '2. Additional air transportation in the U.S.', value: '', error: '' },
        { id: '13.e.3', label: '3. Entertainment and recreation in the U.S.', value: '', error: '' },
        { id: '13.e.4', label: '4. Food and beverages in the U.S.', value: '', error: '' },
        { id: '13.e.5', label: '5. Ground transportation (rail, bus, taxi, etc.) in the U.S.', value: '', error: '' },
        { id: '13.e.6', label: '6. Shopping, gifts, and other purchases in the U.S.', value: '', error: '' },
        { id: '13.e.7', label: '7. Medical services in the U.S.', value: '', error: '' },
        { id: '13.e.8', label: '8. Other spending, if any, in the U.S.', value: '', error: '' }
      ]
    };
  },
  computed: {
    totalValue() {
      return this.inputs.reduce((acc, item) => {
        const { parsedValue } = this.formatAndParseInput(item.value);
        return acc + (isNaN(parsedValue) ? 0 : parsedValue);
      }, 0);
    },
    formattedTotal() {
      return this.totalValue.toLocaleString();
    }
  },
  methods: {
    formatAndParseInput(rawInput) {
      let raw = (rawInput || '').toString().toUpperCase();
      raw = raw.replace(/[^0-9K,]/g, ''); // allow digits, K, commas
      raw = raw.replace(/(K)(?=.)/g, ''); // keep K only at end

      const hasK = raw.endsWith('K');
      let numericPart = raw.replace(/,/g, '');
      if (hasK) numericPart = numericPart.slice(0, -1);

      let parsedValue = numericPart ? parseInt(numericPart, 10) : 0;
      if (hasK) parsedValue *= 1000;

      let formatted = numericPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
      if (hasK) formatted += 'K';

      return { parsedValue, formatted };
    },

    updateAnswer(item) {
      const { parsedValue, formatted } = this.formatAndParseInput(item.value);
      item.value = formatted;

      // Field-level validation
      if (isNaN(parsedValue) || parsedValue < 0) {
        item.error = 'Enter a valid amount (e.g. 80K or 80,000)';
      } else {
        item.error = '';
      }

      // Total-level validation
      const total = this.totalValue;
      if (this.maxAllowed > 0 && total > this.maxAllowed) {
        this.totalError = `Total spending exceeds the allowed limit of declared.`;
        this.$emit('validation-status', true); // ❌ invalid
      } else {
        this.totalError = '';
        this.$emit('validation-status', false); // ✅ valid
      }

      // Save to service
      surveyService.setAnswer(item.id, parsedValue);
    },

    loadAnswers() {
      this.inputs.forEach(item => {
        const saved = surveyService.getAnswer(item.id);
        const displayVal =
          saved && !isNaN(saved)
            ? saved >= 1000 && saved % 1000 === 0
              ? saved / 1000 + 'K'
              : saved.toLocaleString()
            : '';
        item.value = displayVal;
      });
    }
  },
  mounted() {
    this.loadAnswers();
    const max = surveyService.getAnswer('13.d');
    this.maxAllowed = isNaN(max) ? 0 : max;

    // Run validation once on mount
    this.inputs.forEach(item => this.updateAnswer(item));
  }
};
</script>

<style scoped>
.w-100 {
  width: 100% !important;
}
.bg-light {
  background-color: #f8f9fa !important;
}
</style>
